/**
 * TutorialsPage.jsx — Data-driven tutorial page for Healist.AI
 *
 * Reads all content from tutorialData.js. Adding a new tutorial = adding
 * an entry to the data file. Zero component changes needed.
 *
 * Pattern follows ConfigurationGuides.jsx (accordion sections, D4S tokens).
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { TUTORIAL_CATEGORIES, TUTORIALS } from './tutorialData';

const STORAGE_KEY = 'healist_tutorial_progress';

function loadProgress() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveProgress(progress) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

export default function TutorialsPage({ initialTutorialId, setActiveTab }) {
  const [expandedId, setExpandedId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [progress, setProgress] = useState(loadProgress);
  const searchTimerRef = useRef(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const sectionRefs = useRef({});

  // Debounce search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  // Deep link on mount
  useEffect(() => {
    if (initialTutorialId) {
      const tutorial = TUTORIALS.find(t => t.id === initialTutorialId);
      if (tutorial) {
        setActiveCategory(tutorial.category);
        setExpandedId(tutorial.id);
        // Scroll into view after render
        setTimeout(() => {
          const el = sectionRefs.current[tutorial.id];
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [initialTutorialId]);

  // Filter tutorials
  const filteredTutorials = useMemo(() => {
    let results = [...TUTORIALS];

    // Search filter
    if (debouncedQuery.trim()) {
      const q = debouncedQuery.toLowerCase();
      results = results.filter(t =>
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.tags.some(tag => tag.toLowerCase().includes(q)) ||
        t.steps.some(s =>
          s.title.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q)
        )
      );
    } else if (activeCategory !== 'all') {
      // Category filter (only when not searching)
      results = results.filter(t => t.category === activeCategory);
    }

    // Sort by category order then tutorial order
    const catOrder = {};
    TUTORIAL_CATEGORIES.forEach(c => { catOrder[c.id] = c.order; });
    results.sort((a, b) => {
      const catDiff = (catOrder[a.category] || 99) - (catOrder[b.category] || 99);
      return catDiff !== 0 ? catDiff : a.order - b.order;
    });

    return results;
  }, [debouncedQuery, activeCategory]);

  // Category completion counts
  const categoryCounts = useMemo(() => {
    const counts = {};
    TUTORIAL_CATEGORIES.forEach(c => {
      const tutorials = TUTORIALS.filter(t => t.category === c.id);
      const done = tutorials.filter(t => progress[t.id]).length;
      counts[c.id] = { total: tutorials.length, done };
    });
    counts.all = { total: TUTORIALS.length, done: Object.keys(progress).filter(k => progress[k]).length };
    return counts;
  }, [progress]);

  const toggleSection = useCallback((id) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);

  const toggleComplete = useCallback((id) => {
    setProgress(prev => {
      const next = { ...prev, [id]: !prev[id] };
      if (!next[id]) delete next[id];
      saveProgress(next);
      return next;
    });
  }, []);

  const navigateToTutorial = useCallback((id) => {
    const tutorial = TUTORIALS.find(t => t.id === id);
    if (!tutorial) return;
    // If in a different category, switch to it (unless searching)
    if (!debouncedQuery.trim()) {
      setActiveCategory(tutorial.category);
    }
    setExpandedId(id);
    setTimeout(() => {
      const el = sectionRefs.current[id];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [debouncedQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedQuery('');
  };

  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    setSearchQuery('');
    setDebouncedQuery('');
    setExpandedId(null);
  };

  // Result count text
  const resultText = debouncedQuery.trim()
    ? `${filteredTutorials.length} result${filteredTutorials.length !== 1 ? 's' : ''} for "${debouncedQuery}"`
    : `Showing ${filteredTutorials.length} tutorial${filteredTutorials.length !== 1 ? 's' : ''}`;

  return (
    <div className="page-layout tut-container">
      <div className="page-sticky-nav">
        {/* Header */}
        <div className="tut-header">
          <h1 className="tut-title">Tutorials</h1>
          <p className="tut-subtitle">Learn how to get the most out of Healist with step-by-step guides</p>
        </div>

        {/* Search */}
        <div className="tut-search-wrapper">
        <span className="tut-search-icon">{'\u{1F50D}'}</span>
        <input
          className="tut-search-input"
          type="text"
          placeholder="Search tutorials..."
          value={searchQuery}
          onChange={handleSearchChange}
          aria-label="Search tutorials"
        />
        {searchQuery && (
          <button className="tut-search-clear" onClick={clearSearch} aria-label="Clear search">
            &times;
          </button>
        )}
      </div>

      {/* Category Tabs — horizontally scrollable (.tut-tabs is overflow-x:auto with the scrollbar
          hidden), so the container itself must be keyboard-reachable or a keyboard-only user cannot
          scroll it. Fixes the axe `scrollable-region-focusable` (serious) violation on this page. */}
      <div className="tut-tabs" role="tablist" tabIndex={0} aria-label="Tutorial categories">
        <button
          className={`tut-tab ${activeCategory === 'all' ? 'active' : ''}`}
          onClick={() => handleCategoryChange('all')}
          role="tab"
          aria-selected={activeCategory === 'all'}
        >
          All
          <span className="tut-tab-count">({categoryCounts.all.done}/{categoryCounts.all.total})</span>
        </button>
        {TUTORIAL_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={`tut-tab ${activeCategory === cat.id ? 'active' : ''}`}
            onClick={() => handleCategoryChange(cat.id)}
            role="tab"
            aria-selected={activeCategory === cat.id}
          >
            {cat.icon} {cat.label}
            <span className="tut-tab-count">
              ({categoryCounts[cat.id]?.done || 0}/{categoryCounts[cat.id]?.total || 0})
            </span>
          </button>
        ))}
      </div>
      </div>

      <div className="page-scroll-content">
      {/* Result Count */}
      <div className="tut-result-count">{resultText}</div>

      {/* Tutorial Accordions */}
      {filteredTutorials.length === 0 ? (
        <div className="tut-empty">
          <div className="tut-empty-icon">{'\u{1F50D}'}</div>
          <p className="tut-empty-text">No tutorials found. Try a different search term.</p>
        </div>
      ) : (
        filteredTutorials.map(tutorial => {
          const isExpanded = expandedId === tutorial.id;
          const isComplete = !!progress[tutorial.id];

          return (
            <div
              key={tutorial.id}
              className="tut-section"
              ref={el => { sectionRefs.current[tutorial.id] = el; }}
            >
              {/* Header */}
              <div
                className={`tut-section-header ${isExpanded ? 'active' : ''}`}
                onClick={() => toggleSection(tutorial.id)}
              >
                <div className="tut-section-left">
                  <span className="tut-section-icon">{tutorial.icon}</span>
                  <span className="tut-section-title">{tutorial.title}</span>
                </div>
                <div className="tut-section-meta">
                  <span className="tut-section-time">~{tutorial.estimatedMinutes} min</span>
                  {isComplete && <span className="tut-section-check">{'\u2713'}</span>}
                  <span className={`tut-section-chevron ${isExpanded ? 'expanded' : ''}`}>
                    {'\u25BC'}
                  </span>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="tut-section-content">
                  <p className="tut-description">{tutorial.description}</p>

                  {/* Prerequisites */}
                  {tutorial.prerequisites.length > 0 && (
                    <div className="tut-prerequisites">
                      <span className="tut-prerequisites-label">Prerequisites:</span>
                      {tutorial.prerequisites.map(preId => {
                        const pre = TUTORIALS.find(t => t.id === preId);
                        return pre ? (
                          <button
                            key={preId}
                            className="tut-prerequisite-link"
                            onClick={() => navigateToTutorial(preId)}
                          >
                            {pre.icon} {pre.title}
                          </button>
                        ) : null;
                      })}
                    </div>
                  )}

                  {/* Steps */}
                  <ol className="tut-step-list">
                    {tutorial.steps.map((step, idx) => (
                      <li key={idx} className="tut-step">
                        <div className="tut-step-number">{idx + 1}</div>
                        <div className="tut-step-content">
                          <div className="tut-step-title">{step.title}</div>
                          <div className="tut-step-description">{step.description}</div>

                          {step.tip && (
                            <div className="tut-tip-box">
                              <div className="tut-tip-label">{'\u{1F4A1}'} TIP</div>
                              <p className="tut-tip-text">{step.tip}</p>
                            </div>
                          )}

                          {step.warning && (
                            <div className="tut-warning-box">
                              <div className="tut-warning-label">{'\u26A0\uFE0F'} WARNING</div>
                              <p className="tut-warning-text">{step.warning}</p>
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>

                  {/* Notes */}
                  {tutorial.notes && tutorial.notes.length > 0 && (
                    <div className="tut-notes">
                      {tutorial.notes.map((note, idx) => (
                        <div key={idx} className="tut-note-info">
                          <div className="tut-note-info-label">{'\u2139\uFE0F'} NOTE</div>
                          <p className="tut-note-info-text">{note.text}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Related Tutorials */}
                  {tutorial.relatedTutorials.length > 0 && (
                    <div className="tut-related">
                      <span className="tut-related-label">See also:</span>
                      {tutorial.relatedTutorials.map(relId => {
                        const rel = TUTORIALS.find(t => t.id === relId);
                        return rel ? (
                          <button
                            key={relId}
                            className="tut-related-link"
                            onClick={() => navigateToTutorial(relId)}
                          >
                            {rel.icon} {rel.title}
                          </button>
                        ) : null;
                      })}
                    </div>
                  )}

                  {/* Mark Complete */}
                  <div className="tut-complete-wrapper">
                    <button
                      className={`tut-complete-btn ${isComplete ? 'completed' : ''}`}
                      onClick={() => toggleComplete(tutorial.id)}
                    >
                      {isComplete ? '\u2713 Completed' : 'Mark as Complete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
      </div>
    </div>
  );
}
